import { Check, Pencil } from "lucide-react";
import { CONFIRM_LABEL, CONFIRM_NOTICE, needsLogistics, displayVal } from "@/lib/formConfig";

const Row = ({ label, value }) =>
  value ? (
    <div className="py-2.5 flex flex-col gap-0.5 border-b border-slate-100 last:border-0">
      <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">{label}</span>
      <span className="text-sm sm:text-base text-slate-900 leading-relaxed whitespace-pre-line">{value}</span>
    </div>
  ) : null;

const Section = ({ title, onEdit, editTestId, children }) => (
  <div className="rounded-2xl border-2 border-slate-200 bg-white shadow-sm overflow-hidden">
    <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-100 bg-slate-50">
      <h3 className="font-heading font-bold text-slate-900">{title}</h3>
      <button
        type="button"
        data-testid={editTestId}
        onClick={onEdit}
        className="inline-flex items-center gap-1.5 text-sm font-semibold text-rose-600 hover:text-rose-700 transition-colors"
      >
        <Pencil className="h-3.5 w-3.5" /> Edit
      </button>
    </div>
    <div className="px-5 py-2.5">{children}</div>
  </div>
);

export default function StepReview({ data, goToStep, setField }) {
  const logistics = needsLogistics(data.position);
  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Review &amp; Submit</h2>
        <p className="text-sm sm:text-base text-slate-600 mt-1">
          Make sure everything looks right — tap Edit on any section to jump back.
        </p>
      </div>

      <Section title="Personal Information" editTestId="review-edit-personal-btn" onEdit={() => goToStep(1)}>
        <Row label="Position" value={data.position} />
        <Row label="Name" value={`${data.firstName} ${data.lastName}`.trim()} />
        <Row label="Phone" value={data.phone} />
        <Row label="Email" value={data.email} />
        <Row label="Address" value={data.address} />
        <Row label="18 or older" value={data.is18} />
        <Row label="Authorized to work in the U.S." value={data.workAuth} />
        <Row label="Instagram / Social" value={data.instagram} />
        <Row label="Shirt size" value={data.shirtSize} />
      </Section>

      <Section title="Experience" editTestId="review-edit-experience-btn" onEdit={() => goToStep(2)}>
        <Row label="Worked events before" value={data.workedEvents} />
        <Row label="Event types" value={(data.eventTypes || []).join(", ")} />
        <Row label="Experience" value={data.describeExperience} />
        <Row label="Tech comfort" value={data.techComfort} />
        <Row label="Familiar with" value={(data.familiarTech || []).join(", ")} />
        <Row label="About you" value={data.aboutYou} />
        <Row label="Professional traits" value={(data.traits || []).join(", ")} />
      </Section>

      <Section title="Availability" editTestId="review-edit-availability-btn" onEdit={() => goToStep(3)}>
        <Row label="Available days" value={data.availableDays} />
        <Row label="Early mornings / late nights" value={displayVal(data, "earlyLate")} />
        <Row label="Commute to NYC" value={data.nycCommute} />
        <Row label="Commute to Long Island" value={data.liCommute} />
        <Row label="Availability window" value={data.availabilityWindow} />
      </Section>

      <Section title="Transportation" editTestId="review-edit-requirements-btn" onEdit={() => goToStep(4)}>
        {logistics ? (
          <>
            <Row label="Valid driver's license" value={data.driversLicense} />
            <Row label="Years of driving experience" value={data.yearsDriving} />
            <Row label="Comfortable driving cargo vans" value={displayVal(data, "cargoVan")} />
            <Row label="Lift 50+ lbs" value={data.lift50} />
            <Row label="Loading / transporting equipment" value={data.loadTransport} />
          </>
        ) : (
          <p className="py-3 text-sm text-slate-500">Not applicable — Photo Booth Event Staff.</p>
        )}
      </Section>

      <Section title="Job Requirements" editTestId="review-edit-job-requirements-btn" onEdit={() => goToStep(4)}>
        <Row label="Physical requirements" value={data.physicalAck} />
        <Row label="Appearance standard" value={data.dressCode} />
        <Row label="Why CLiQ! Booths" value={data.whyCliq} />
        <Row label="Anything else" value={data.anythingElse} />
      </Section>

      <div className="rounded-2xl border-2 border-slate-200 bg-white shadow-sm p-5 space-y-4">
        <p className="text-sm text-slate-500 leading-relaxed">{CONFIRM_NOTICE}</p>
        <button
          type="button"
          data-testid="confirm-info-card"
          aria-pressed={data.confirm}
          onClick={() => setField("confirm", !data.confirm)}
          className={`w-full text-left px-4 py-4 rounded-2xl border-2 transition-all duration-200 active:scale-[0.99] focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-500/40 flex items-start gap-3.5 ${
            data.confirm ? "border-rose-600 bg-rose-50 shadow-md" : "border-slate-200 bg-white hover:border-slate-400"
          }`}
        >
          <span
            className={`shrink-0 mt-0.5 h-6 w-6 rounded-lg border-2 grid place-items-center transition-colors ${
              data.confirm ? "bg-rose-600 border-rose-600" : "border-slate-300 bg-white"
            }`}
          >
            {data.confirm && <Check className="h-4 w-4 text-white" strokeWidth={3.5} />}
          </span>
          <span className="text-sm sm:text-base font-semibold text-slate-900 leading-relaxed">{CONFIRM_LABEL}</span>
        </button>
      </div>
    </div>
  );
}
